import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { NvAlertDialog, NvButton, NvConsentPromptDialog, NvDropdownField, NvFormField, NvRequiredLabel, NvTermsModal } from "@/components/navigator";
import { getApiError, getApiFieldErrors } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import {
  REQUIRED_SIGNUP_TERMS,
  SIGNUP_TERMS,
  signup,
  uploadBusinessLicense,
  verifyEmail,
  type SignupPayload,
  type SignupUserType,
} from "@/services/navigator/signup-service";

import { SignupInvitePage } from "./signup-invite-page";
import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
  LoginHint,
  OPTIONAL_PROMPT,
  OPTIONAL_TERMS_CODE,
  SIGNUP_TERMS_TAB_LABELS,
  SectionTitle,
  SignupHeading,
  SignupShell,
  SignupTypeHint,
  SubmitRow,
  TermsBox,
  formatBusinessNo,
  formatPhone,
  hasOptionalConsent,
  sanitizeName,
  validEmail,
  validName,
} from "./signup-parts";

// F06 회원가입 (Figma ver.7 07 · 설계서 Front v0.5 slide 88~94). 수요자: 회사명 · 대표명 직접 입력, 기업 소재국(기본 한국), 부서 · 직급, 이름 · 전화번호 · 이메일 + [인증], 약관 4종.
// 공급자: 이름 · 전화번호 · 이메일 + [인증], 회사명(50자) · 사업자 등록 번호 · 사업자 등록증 사본(PDF), 약관 4종.
// 수요자 · 공급자 모두 [가입 신청] · [회원가입]을 누르면 (제3자 제공 미동의 시 "선택 동의 미동의 안내" 팝업 →) 입력 정보 확인 팝업을 거쳐 가입해요.
// 약관은 "모두 동의합니다" 묶음 + 제3자 제공 (선택)이고, 미동의로 가입해도 약관 동의 기록의 agreed=false 로 남아요.
// 이메일 [인증]은 등록 공급사 · 기가입 · 초대 메일 발송 3갈래 팝업이고, 인증을 마친 이메일이어야 가입 신청이 활성화돼요.
// 비밀번호 · 직책은 받지 않아요(로그인은 Outlook · PwC SSO 전용).

const EMPTY_FORM = {
  company_name: "",
  ceo_name: "",
  company_country: DEFAULT_COUNTRY,
  department: "",
  position: "",
  requester_name: "",
  phone: "",
  email: "",
  business_reg_no: "",
};
type FormState = typeof EMPTY_FORM;

interface Popup {
  message: string;
  button: string;
}

function SelfSignup() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<SignupUserType>("demand");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [termsTab, setTermsTab] = useState<string | null>(null);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [optionalPromptOpen, setOptionalPromptOpen] = useState(false);
  const [verified, setVerified] = useState<{ email: string; token: string } | null>(null);
  const [blockedEmail, setBlockedEmail] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [license, setLicense] = useState<{ id: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const supplier = userType === "supplier";
  const email = form.email.trim().toLowerCase();
  const isVerified = verified !== null && verified.email === email;
  const blocked = !supplier && blockedEmail !== null && blockedEmail === email;
  const allAgreed = REQUIRED_SIGNUP_TERMS.every((code) => consents[code]);

  const set = (key: keyof FormState) => (value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
  };

  const fieldErrors = (): Record<string, string> => {
    const out: Record<string, string> = {};
    if (!form.company_name.trim()) out.company_name = "필수 입력 항목입니다.";
    if (!validName(form.requester_name)) out.requester_name = "한글 5자 · 영문 20자 이내로 입력해 주세요.";
    if (form.phone.replace(/\D/g, "").length < 9) out.phone = "전화번호를 확인해 주세요.";
    if (!validEmail(form.email)) out.email = "이메일 형식을 확인해 주세요.";
    if (supplier) {
      if (form.business_reg_no.replace(/\D/g, "").length !== 10) out.business_reg_no = "사업자 등록 번호 10자리를 입력해 주세요.";
      if (!license) out.business_license = "PDF 파일을 첨부해 주세요.";
    } else {
      if (!form.ceo_name.trim()) out.ceo_name = "필수 입력 항목입니다.";
      if (!form.company_country) out.company_country = "소재국을 선택해 주세요.";
      if (!form.department.trim()) out.department = "필수 입력 항목입니다.";
      if (!form.position.trim()) out.position = "필수 입력 항목입니다.";
    }
    return out;
  };
  const canSubmit = !submitting && isVerified && !blocked && allAgreed && Object.keys(fieldErrors()).length === 0;

  const switchType = (type: SignupUserType) => {
    setUserType(type);
    setErrors({});
    setBlockedEmail(null);
  };

  const onVerify = async () => {
    if (!validEmail(form.email)) {
      setErrors((e) => ({ ...e, email: "이메일 형식을 확인해 주세요." }));
      return;
    }
    setVerifying(true);
    try {
      const result = await verifyEmail(userType, email);
      if (result.result === "registered_supplier") {
        setBlockedEmail(email);
        setVerified(null);
        setPopup({ message: "정책상 등록된 공급사에서는 수요회원으로 가입이 불가합니다.", button: "확인" });
      } else if (result.result === "already_registered") {
        setVerified(null);
        setPopup({ message: "이미 가입된 이메일입니다. 해당 이메일로 로그인해 주세요.", button: "닫기" });
      } else if (result.verification_token) {
        setVerified({ email, token: result.verification_token });
        // 설계서 문구는 메일이 실제로 나갔을 때만 맞아요. Graph 초대가 꺼져 있으면(권한 승인 전) 메일을 안 보냈으니 사실대로 안내
        setPopup({
          message:
            result.mail_sent === false
              ? "이메일 인증이 완료되었습니다. (초대 메일 발송은 준비 중입니다.)\n관리자 승인이 완료되면 로그인하실 수 있습니다."
              : "입력하신 이메일로 초대 메일을 발송했습니다. 메일함에서 초대를 수락해 주세요.\n관리자 승인이 완료되면 로그인하실 수 있습니다.",
          button: "확인",
        });
      }
    } catch (error) {
      const apiError = getApiError(error);
      setPopup({
        message: apiError?.code === "PWC_DOMAIN" ? "PwC 임직원은 회원가입 없이 PwC SSO로 로그인할 수 있습니다." : (apiError?.message ?? "이메일 인증을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요."),
        button: "확인",
      });
    } finally {
      setVerifying(false);
    }
  };

  const onPickLicense = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setErrors((e) => ({ ...e, business_license: "" }));
    try {
      const result = await uploadBusinessLicense(file);
      setLicense({ id: result.document_id, name: result.filename });
    } catch (error) {
      setLicense(null);
      setErrors((e) => ({ ...e, business_license: getApiError(error)?.message ?? "PDF 파일을 첨부해 주세요." }));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  // checked: 팝업 [동의하고 가입하기] 직후처럼 화면 상태가 아직 반영되기 전의 동의 값
  const submit = async (checked: Record<string, boolean> = consents) => {
    if (!verified) return;
    const payload: SignupPayload = {
      user_type: userType,
      company_name: form.company_name.trim(),
      requester_name: form.requester_name.trim(),
      phone: form.phone.replace(/\D/g, ""),
      email,
      verification_token: verified.token,
      consents: SIGNUP_TERMS.filter((code) => checked[code]),
      ...(supplier
        ? { business_reg_no: form.business_reg_no.replace(/\D/g, ""), business_license_doc_id: license?.id ?? null }
        : { ceo_name: form.ceo_name.trim(), company_country: form.company_country, department: form.department.trim(), position: form.position.trim() }),
    };
    setSubmitting(true);
    try {
      await signup(payload);
      navigate("/signup/complete", { replace: true });
    } catch (error) {
      const apiError = getApiError(error);
      switch (apiError?.code) {
        case "REGISTERED_SUPPLIER_EMAIL":
          setBlockedEmail(email);
          setPopup({ message: "정책상 등록된 공급사에서는 수요회원으로 가입이 불가합니다.", button: "확인" });
          break;
        case "ALREADY_REGISTERED":
          setPopup({ message: "이미 가입된 이메일입니다. 해당 이메일로 로그인해 주세요.", button: "닫기" });
          break;
        case "PWC_DOMAIN":
          setPopup({ message: "PwC 임직원은 회원가입 없이 PwC SSO로 로그인할 수 있습니다.", button: "확인" });
          break;
        default: {
          const found = getApiFieldErrors(error);
          setErrors((e) => ({ ...e, ...found }));
          // 인증 토큰이 만료됐으면 다시 인증하도록
          if (found.verification_token) setVerified(null);
          setPopup({ message: apiError?.message ?? "가입 신청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.", button: "확인" });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = () => {
    const found = fieldErrors();
    setErrors(found);
    if (!canSubmit) return;
    // 선택 동의(제3자 제공)를 안 했으면 먼저 선택 동의 미동의 안내 팝업, 그다음 입력 정보 확인 팝업 (수요자 · 공급자 공통, slide 91)
    if (!hasOptionalConsent(consents)) setOptionalPromptOpen(true);
    else setConfirmOpen(true);
  };

  // 선택 동의 미동의 안내 팝업의 두 버튼: [동의하고 가입하기] / [동의 없이 가입하기] → 입력 정보 확인 팝업
  const continueAfterOptionalPrompt = (agree: boolean) => {
    if (agree) setConsents((c) => ({ ...c, [OPTIONAL_TERMS_CODE]: true }));
    setOptionalPromptOpen(false);
    setConfirmOpen(true);
  };

  const emailField = (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-start gap-2">
        <NvFormField
          className="flex-1"
          inputClassName="rounded-nv-card"
          label={<NvRequiredLabel>이메일</NvRequiredLabel>}
          aria-required
          type="email"
          placeholder="example@company.com"
          value={form.email}
          onChange={(e) => set("email")(e.target.value)}
          error={errors.email}
          autoComplete="email"
        />
        <NvButton type="button" variant="secondary" className="mt-[30px]" onClick={onVerify} disabled={verifying || !form.email.trim()}>
          인증
        </NvButton>
      </div>
      {isVerified && <p className="text-[12px] leading-[14px] text-[#059669]">인증을 마쳤어요. 초대 메일의 초대를 수락해 주세요.</p>}
    </div>
  );

  const nameField = (
    <NvFormField
      label={<NvRequiredLabel>이름</NvRequiredLabel>}
      aria-required
      placeholder="한글 5자 · 영문 20자 이내"
      value={form.requester_name}
      onChange={(e) => set("requester_name")(sanitizeName(e.target.value))}
      error={errors.requester_name}
      autoComplete="name"
    />
  );
  const phoneField = (
    <NvFormField label={<NvRequiredLabel>전화번호</NvRequiredLabel>} aria-required placeholder="010-0000-0000" inputMode="tel" value={form.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} error={errors.phone} />
  );

  return (
    <SignupShell onSubmit={onSubmit}>
      <SignupHeading title="회원가입" description="가입 신청 후 관리자 승인을 거쳐 이용할 수 있습니다" />

      <div role="tablist" aria-label="사용자 유형" className="flex w-fit gap-[2px] rounded-nv-chip bg-nv-bg-page p-[3px]">
        {(["demand", "supplier"] as const).map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={userType === type}
            onClick={() => switchType(type)}
            className={cn("w-20 rounded-nv-chip py-2 text-[13px] font-bold leading-4", userType === type ? "bg-nv-text-primary text-nv-text-inverse" : "text-nv-text-secondary hover:text-nv-text-primary")}
          >
            {type === "demand" ? "수요자" : "공급자"}
          </button>
        ))}
      </div>
      <SignupTypeHint />

      {supplier ? (
        <>
          <SectionTitle>기본 정보</SectionTitle>
          <div className="grid gap-3 md:grid-cols-2">
            {nameField}
            {phoneField}
          </div>
          {emailField}
          <NvFormField inputClassName="rounded-nv-card" label={<NvRequiredLabel>회사명</NvRequiredLabel>} aria-required placeholder="회사명을 입력해 주세요 (50자 이내)" maxLength={50} value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} error={errors.company_name} />
          <NvFormField
            inputClassName="rounded-nv-card"
            label={<NvRequiredLabel>사업자 등록 번호</NvRequiredLabel>}
            aria-required
            placeholder="사업자 등록 번호 10자리를 입력해 주세요"
            inputMode="numeric"
            value={form.business_reg_no}
            onChange={(e) => set("business_reg_no")(formatBusinessNo(e.target.value))}
            error={errors.business_reg_no}
          />
          <div className="flex flex-col gap-[6px]">
            <span className="text-nv-label-md text-nv-text-strong">
              <NvRequiredLabel>사업자 등록증 사본 (PDF)</NvRequiredLabel>
            </span>
            <div className="flex items-start gap-2">
              <div className={cn("flex h-12 flex-1 items-center truncate rounded-nv-card border bg-nv-surface px-[14px] text-nv-body-md", errors.business_license ? "border-nv-danger" : "border-nv-border-strong", license ? "text-nv-text-strong" : "text-nv-text-secondary")}>
                {license?.name ?? "PDF 파일을 첨부해 주세요"}
              </div>
              <input ref={fileInput} type="file" accept="application/pdf,.pdf" className="sr-only" tabIndex={-1} onChange={(e) => onPickLicense(e.target.files?.[0])} />
              <NvButton type="button" variant="secondary" className="mt-[2px]" disabled={uploading} onClick={() => fileInput.current?.click()}>
                파일 선택
              </NvButton>
            </div>
            {errors.business_license && <p className="text-nv-caption text-nv-danger">{errors.business_license}</p>}
          </div>
        </>
      ) : (
        <>
          <SectionTitle>회사 정보</SectionTitle>
          <NvFormField inputClassName="rounded-nv-card" label={<NvRequiredLabel>회사명</NvRequiredLabel>} aria-required placeholder="회사명을 입력해 주세요" maxLength={100} value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} error={errors.company_name} />
          <div className="grid gap-3 md:grid-cols-2">
            <NvFormField label={<NvRequiredLabel>대표명</NvRequiredLabel>} aria-required placeholder="대표명을 입력해 주세요" maxLength={100} value={form.ceo_name} onChange={(e) => set("ceo_name")(e.target.value)} error={errors.ceo_name} />
            <NvDropdownField label={<NvRequiredLabel>기업 소재국</NvRequiredLabel>} placeholder="소재국을 선택해 주세요" options={COUNTRY_OPTIONS} value={form.company_country} onChange={set("company_country")} error={errors.company_country} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <NvFormField label="부서" required placeholder="부서를 입력해 주세요" maxLength={100} value={form.department} onChange={(e) => set("department")(e.target.value)} error={errors.department} />
            <NvFormField label="직급" required placeholder="직급을 입력해 주세요" maxLength={100} value={form.position} onChange={(e) => set("position")(e.target.value)} error={errors.position} />
          </div>
          <SectionTitle>계정 정보</SectionTitle>
          <div className="grid gap-3 md:grid-cols-2">
            {nameField}
            {phoneField}
          </div>
          {emailField}
        </>
      )}

      <TermsBox consents={consents} onChange={setConsents} onView={setTermsTab} />
      <SubmitRow label={supplier ? "회원가입" : "가입 신청"} disabled={!canSubmit} />
      <LoginHint />

      <NvConsentPromptDialog
        open={optionalPromptOpen}
        onOpenChange={setOptionalPromptOpen}
        title={OPTIONAL_PROMPT.title}
        message={OPTIONAL_PROMPT.message}
        itemLabel={OPTIONAL_PROMPT.itemLabel}
        declineLabel={OPTIONAL_PROMPT.declineLabel}
        agreeLabel={OPTIONAL_PROMPT.agreeLabel}
        onView={() => setTermsTab(OPTIONAL_TERMS_CODE)}
        onDecline={() => continueAfterOptionalPrompt(false)}
        onAgree={() => continueAfterOptionalPrompt(true)}
      />
      <NvTermsModal codes={[...SIGNUP_TERMS]} openCode={termsTab} onOpenCodeChange={setTermsTab} labels={SIGNUP_TERMS_TAB_LABELS} />

      <NvAlertDialog open={popup !== null} onOpenChange={(open) => !open && setPopup(null)} message={popup?.message} actions={<NvButton onClick={() => setPopup(null)}>{popup?.button}</NvButton>} />
      <NvAlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        message={`입력하신 정보가 맞는지 확인해 주세요.\n이름 : ${form.requester_name}\n전화번호 : ${form.phone}\n이메일 : ${form.email.trim()}`}
        actions={
          <>
            <NvButton type="button" variant="secondary" onClick={() => setConfirmOpen(false)}>
              취소
            </NvButton>
            <NvButton
              type="button"
              className="self-stretch"
              onClick={() => {
                setConfirmOpen(false);
                void submit();
              }}
            >
              확인
            </NvButton>
          </>
        }
      />
    </SignupShell>
  );
}

export function NavigatorSignupPage() {
  const [params] = useSearchParams();
  const invite = params.get("invite");
  // 사전 초대 링크(?invite=)로 들어오면 초대 가입 흐름 (slide 95 · 97)
  if (invite) return <SignupInvitePage token={invite} fallback={<SelfSignup />} />;
  return <SelfSignup />;
}
